"use client"
import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { lazy, Suspense } from "react"
import {
  Calendar,
  Menu,
  X,
  ChevronDown,
  Play,
  ArrowRight,
  Check,
  Zap,
  Mic,
  Database,
  RefreshCw,
  Lock,
  Smartphone,
  LogIn,
  CreditCard,
  Settings,
  Briefcase,
  Clock,
  UserPlus,
  Rocket,
} from "lucide-react"

import { MyteConstructionHelmet } from "../../../seo/helmetConfigs"
// Dynamically import ThreeSphere to avoid loading it on mobile or in SSR
const ThreeSphere = lazy(() => import("../../AMyteSphere/ThreeSphere"))


/* -------------------------------------------------------------------------- */
/*                           1. CONSTANTS & DATA                              */
/* -------------------------------------------------------------------------- */
// Note: We have not changed any text, descriptions, icons, or links.

const productCards = [
  {
    title: "Myte Timesheet",
    description: "Speak timesheets directly using predefined teams and cost codes. Save 5+ hours weekly per foreman.",
    icon: <Mic className="w-8 h-8 text-blue-400" />,
    link: "https://mytegroup.ai/timesheets",
    linkText: "Try Now",
  },
  {
    title: "Myte Material Requests",
    description: "Voice-request your tools and equipment, automate tracking, and manage real-time approvals.",
    icon: <Database className="w-8 h-8 text-blue-400" />,
    link: "https://mytegroup.ai/equipment-request",
    linkText: "Try Now",
  },
  {
    title: "Myte Business Cards",
    description:
      "Convert hundreds of business card images into verified data and an Excel spreadsheet of contacts ready to use.",
    icon: <CreditCard className="w-8 h-8 text-blue-400" />,
    link: "https://mytegroup.ai/business-cards",
    linkText: "Try Now",
  },
  {
    title: "Business Automation - Custom Reasoning Systems",
    description: "Leverage custom AI reasoning systems to automate complex business processes.",
    icon: <Settings className="w-8 h-8 text-blue-400" />,
    link: "https://www.mytecody.com",
    linkText: "Explore Myte Cody",
  },
  {
    title: "Features Coming Soon",
    description: "RFIs, Change Orders, Field Reports, Pick Lists, Purchase Orders, and more!!",
    icon: <Rocket className="w-8 h-8 text-blue-400" />,
    link: "https://calendly.com/ahmed-mekallach/generative_ai_discovery_call",
    linkText: "Book a Call",
  },
]

const featuresArray = [
  {
    title: "Voice Timesheets",
    desc: "Eliminate tedious manual entry. Speak your crew's cost-coded hours directly.",
  },
  {
    title: "Real-Time Payroll",
    desc: "Immediate insights into labor costs—validated and ready for payroll in seconds.",
  },
  {
    title: "AI-Powered Data",
    desc: "Simply upload existing Excel sheets—AI automatically populates your datasets accurately.",
  },
  {
    title: "Universal and Limitless Integrations",
    desc: "Integrate or overlay on anything you want, however you want. Universal and limitless integrations.",
  },
  {
    title: "Controlled Access & Security",
    desc: "Company-controlled user invitations ensure secure, role-specific access.",
  },
  {
    title: "Mobile Optimized",
    desc: "Access from anywhere—in the field, office, or on the move with any device.",
  },
]

const communityPartners = [
  {
    name: "Maria Meriano",
    company: "Les Diamants Inc.",
    description: "Pioneering innovative recruitment solutions with AI-Centric Computer Operators",
    avatar: "/Visionaries/MariaM.jpeg",
    link: "https://www.les-diamants.com/en/",
  },
  {
    name: "Maria Vilshenski",
    company: "Silo‑IN",
    description: "A Founder and CEO creating AI Centric HR-Tech in construction",
    avatar: "/Visionaries/MariaV.jpeg",
    link: "https://silo-in.com",
  },
  {
    name: "Dr. Marco Mascarella",
    company: "Jewish General Hospital",
    description: "Innovator in the HealthTech space revolutionizing workflows with Myte Health Connect",
    avatar: "/Visionaries/MarcoM.jpeg",
    link: "https://www.mcgill.ca/ent/faculty/marco-mascarella-md-msc-phdc",
  },
  {
    name: "Ragheed Johier",
    company: "CourtierXpert",
    description: "Trailbrazer in the real-estate space, digitalizing his workflows with AI",
    avatar: "/Visionaries/RagheedJ.jpeg",
    link: "https://www.courtierxpert.com/home",
  },
]

const featureIcons = {
  "Voice Timesheets": <Mic className="w-10 h-10 mb-4 text-blue-400" />,
  "Real-Time Payroll": <Zap className="w-10 h-10 mb-4 text-blue-400" />,
  "AI-Powered Data": <Database className="w-10 h-10 mb-4 text-blue-400" />,
  "Universal and Limitless Integrations": <RefreshCw className="w-10 h-10 mb-4 text-blue-400" />,
  "Controlled Access & Security": <Lock className="w-10 h-10 mb-4 text-blue-400" />,
  "Mobile Optimized": <Smartphone className="w-10 h-10 mb-4 text-blue-400" />,
}

/* -------------------------------------------------------------------------- */
/*                           2. UTILITY HOOKS                                 */
/* -------------------------------------------------------------------------- */

// Simple IntersectionObserver Hook to highlight nav
function useActiveSection(setActiveSection) {
  useEffect(() => {
    let timeoutId = null
    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        const scrollPosition = window.scrollY + 100
        const sections = document.querySelectorAll("section[id]") || []
        sections.forEach((section) => {
          const sectionTop = section.offsetTop
          const sectionHeight = section.offsetHeight
          const sectionId = section.getAttribute("id") || ""
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(sectionId)
          }
        })
      }, 100)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [setActiveSection])
}

/**
 * Very simple typed effect logic
 */
export function useFastTypewriter(typedElementRef, setShowTagline) {
  useEffect(() => {
    const phrases = [
      "Speak Your Job Site Needs into Existence",
      "Own Your Construction Technology",
      "Build Custom Solutions That Work for You",
      "Transform Data Entry with Voice Commands",
      "Automate Your Construction Workflow",
    ]
    let phraseIndex = 0
    let animationFrame
    const totalTypingTime = 500
    const totalDeletingTime = 500
    let isMounted = true

    const typePhrase = (phrase, cb) => {
      let start = null
      const update = (timestamp) => {
        if (!start) start = timestamp
        const elapsed = timestamp - start
        const progress = Math.min(elapsed / totalTypingTime, 1)
        const charCount = Math.floor(phrase.length * progress)
        if (typedElementRef.current) {
          typedElementRef.current.textContent = phrase.slice(0, charCount)
        }
        if (progress < 1) {
          animationFrame = requestAnimationFrame(update)
        } else {
          cb()
        }
      }
      animationFrame = requestAnimationFrame(update)
    }

    const deletePhrase = (phrase, cb) => {
      let start = null
      const update = (timestamp) => {
        if (!start) start = timestamp
        const elapsed = timestamp - start
        const progress = Math.min(elapsed / totalDeletingTime, 1)
        const charCount = phrase.length - Math.floor(phrase.length * progress)
        if (typedElementRef.current) {
          typedElementRef.current.textContent = phrase.slice(0, charCount)
        }
        if (progress < 1) {
          animationFrame = requestAnimationFrame(update)
        } else {
          cb()
        }
      }
      animationFrame = requestAnimationFrame(update)
    }

    const cyclePhrases = () => {
      if (!isMounted || !typedElementRef.current) return
      const currentPhrase = phrases[phraseIndex]
      typePhrase(currentPhrase, () => {
        setTimeout(() => {
          deletePhrase(currentPhrase, () => {
            phraseIndex = (phraseIndex + 1) % phrases.length
            cyclePhrases()
          })
        }, 1000)
      })
    }

    cyclePhrases()
    setShowTagline(true)

    return () => {
      isMounted = false
      cancelAnimationFrame(animationFrame)
    }
  }, [typedElementRef, setShowTagline])
}

/* -------------------------------------------------------------------------- */
/*                         3. SUBCOMPONENTS (HEADER, HERO, ETC.)             */
/* -------------------------------------------------------------------------- */

// 1) HEADER
function Header({ isScrolled, isMenuOpen, setIsMenuOpen, activeSection }) {
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-[#0e1d34]/90 backdrop-blur-md shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex justify-center">
          <Link to="/myteconstruction" className="flex items-center">
            <img
              src="/LogoFile.png"
              alt="Myte Construction Logo"
              className="w-36 h-auto max-w-full object-contain"
              loading="lazy"
            />
          </Link>
        </div>
        {/* Main Nav */}
        <nav className="flex items-center gap-6">
          <div className="hidden md:flex gap-6">
            <a
              href="#products"
              className={`text-sm font-medium ${
                activeSection === "products" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Products
            </a>
            <a
              href="#features"
              className={`text-sm font-medium ${
                activeSection === "features" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Features
            </a>
            <a
              href="#pricing"
              className={`text-sm font-medium ${
                activeSection === "pricing" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Pricing
            </a>
            <Link
              to="/myteconstruction/video-demos"
              className={`text-sm font-medium ${
                activeSection === "video-demos" ? "text-white" : "text-blue-200"
              } hover:text-white transition-colors duration-300`}
            >
              Video Demos
            </Link>
          </div>
          {/* Buttons (Login / Schedule a Call) */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/myteconstruction/login"
              className="inline-flex items-center px-3 py-2 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg transition-all duration-300"
            >
              <LogIn className="w-4 h-4 mr-2" /> Login/Register
            </Link>
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
            </a>
          </div>
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white bg-transparent border-none"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>
      {/* Mobile Dropdown Nav */}
      {isMenuOpen && (
        <div className="md:hidden bg-[#0e1d34]/95 backdrop-blur-md p-4">
          <nav className="flex flex-col gap-4 max-w-7xl mx-auto">
            <a
              href="#products"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Products
            </a>
            <a
              href="#features"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Features
            </a>
            <a
              href="#pricing"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Pricing
            </a>
            <Link
              to="/myteconstruction/video-demos"
              className="text-lg text-blue-200 hover:text-white transition-colors duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              Video Demos
            </Link>
            <Link
              to="/myteconstruction/login"
              className="flex items-center px-4 py-3 bg-transparent hover:bg-[#3080c1]/20 text-white rounded-lg transition-all duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <LogIn className="w-4 h-4 mr-2" /> Login/Register
            </Link>
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center px-4 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300"
              onClick={() => setIsMenuOpen(false)}
            >
              <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
            </a>
          </nav>
        </div>
      )}
    </header>
  )
}
function HeroSection({ showTagline, typedElementRef }) {
  const textToAnimate = "Build, Mold and Steer Your Tech";
  const [zoomMultiplier, setZoomMultiplier] = useState(5.5);
  const [isMobile, setIsMobile] = useState(false);

  // Handle resizing for sphere
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setZoomMultiplier(mobile ? 3 : 5.5);
    };
    checkMobile();
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(checkMobile, 100);
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section
      id="hero"
      className="pt-32 pb-4 px-4 relative overflow-visible lg:pt-40 lg:pb-8 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a]"
    >
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left Side: Text Content */}
          <div className="text-center lg:text-left">
            {showTagline && (
              <h2 className="text-xl md:text-2xl text-[#3080c1] font-semibold mb-4 lively-chars">
                {Array.from(textToAnimate).map((char, idx) => (
                  <span
                    key={idx}
                    className="inline-block"
                    style={{
                      animation: `livelyChar 0.4s ease-out forwards`,
                      animationDelay: `${0.03 * idx}s`,
                      opacity: 0,
                      willChange: "opacity, transform",
                    }}
                  >
                    {char === " " ? "\u00A0" : char}
                  </span>
                ))}
              </h2>
            )}

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-white transition-all duration-700 hover:scale-105">
              Transform Your Job-Site With AI-Driven Efficiency
            </h1>

            <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto lg:mx-0">
              Cut 5+ hours of admin every week. Myte Construction Suite turns voice commands into timesheets, material requests, and more—customized or ready out of the box.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start relative z-20">
              <a
                href="https://www.mytegroup.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#3080c1]/30 flex items-center justify-center"
              >
                Start Free – Save Time Instantly
                <Rocket className="ml-2 w-4 h-4" />
              </a>

              <Link
                to="/myteconstruction/video-demos"
                className="px-6 py-3 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-[#1e3a8a]/30 flex items-center justify-center"
              >
                <Play className="mr-2 w-4 h-4" /> See It in Action
              </Link>
            </div>

            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold mt-6 leading-tight min-h-[100px]"
              aria-label="AI construction technology benefits"
            >
              <span id="typed-headlines" ref={typedElementRef}></span>
            </h1>
          </div>

          {/* Right Side: Sphere */}
          <div className="hidden lg:block relative">
            <div className="sphere-container absolute top-0 right-[-5%] w-full h-full flex justify-center items-center">
              {!isMobile && (
                <Suspense
                  fallback={
                    <div className="sphere-placeholder h-[400px] md:h-[500px] lg:h-[600px] bg-[#1e3a8a]/20 rounded-xl" />
                  }
                >
                  <ThreeSphere zoomMultiplier={zoomMultiplier} />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 cursor-pointer group">
        <a href="#products" className="block">
          <ChevronDown className="w-8 h-8 text-blue-400 animate-bounce group-hover:text-white transition-colors duration-300" />
          <span className="text-xs text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300 absolute left-1/2 transform -translate-x-1/2 mt-1">
            Scroll Down
          </span>
        </a>
      </div>
    </section>
  );
}


/* -------------- Products Section -------------- */
function ProductsSection({ isVisible }) {
  return (
    <section
      id="products"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.products ? "opacity-100 bg-gradient-to-br from-[#1e3a8a] to-[#3080c1]/10" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
            Explore What's Possible with Myte
          </h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            From voice-enabled timesheets to complete business automation—including Myte Business Cards that convert
            hundreds of card images into verified data—discover how Myte can transform your workflow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {productCards.map((product, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-6 rounded-xl border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1`}
            >
              <div className="flex items-center mb-4">
                {product.icon}
                <h3 className="text-xl font-bold ml-3 text-blue-100 hover:text-blue-300 transition-colors duration-300">
                  {product.title}
                </h3>
              </div>
              <p className="text-blue-200 mb-6 h-24">{product.description}</p>
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300"
              >
                {product.linkText} <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------- About Section -------------- */
function AboutSection({ isVisible }) {
  return (
    <section
      id="about"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.about ? "opacity-100 bg-gradient-to-br from-[#3080c1]/10 to-[#0e1d34]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="order-2 md:order-1">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-blue-100">Built by Someone Who Gets It</h2>
            <p className="text-base md:text-lg text-blue-200 mb-4">
              I'm Ahmed Mekallach, and I've been in your boots—literally. From the field to the office, I've seen the
              pain of manual payroll and data entry firsthand. Myte Construction is built to solve those problems.
            </p>
            <p className="text-base md:text-lg text-blue-200">
              Our AI-driven, voice-enabled payroll and timesheet system is designed for the tough demands of
              construction crews. Say goodbye to tedious manual data entry—speak your inputs, get instant verification,
              and sync seamlessly with your existing tools.
            </p>
          </div>
          <div className="flex justify-center order-1 md:order-2">
            <div className="relative w-48 h-48 md:w-64 md:h-64">
              <div className="absolute inset-0 bg-[#3080c1]/20 rounded-full blur-xl"></div>
              <div className="w-full h-full rounded-full border-4 border-[#3080c1] shadow-xl relative z-10 overflow-hidden">
                <img
                  src="/AhmedStageImpact.jpg"
                  alt="Ahmed Mekallach in the Field with Hardhat"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* -------------- Features Section -------------- */
function FeaturesSection({ isVisible }) {
  return (
    <section
      id="features"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.features ? "opacity-100 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">Key Features</h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Revolutionizing payroll management for field construction crews with powerful, easy-to-use tools designed
            specifically for your workflow.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresArray.map((feature, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-6 rounded-xl border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1`}
            >
              {featureIcons[feature.title]}
              <h3 className="text-xl font-bold mb-2 text-[#3080c1] hover:text-blue-300 transition-colors duration-300">
                {feature.title}
              </h3>
              <p className="text-blue-200">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------- Benefits Section -------------- */
function BenefitsSection({ isVisible }) {
  const benefitsData = [
    {
      title: "Save Hours Every Week",
      desc: "Reduce data entry from hours to minutes—no more errors or delays.",
      icon: <Clock className="w-12 h-12" />,
    },
    {
      title: "Instant Data Clarity",
      desc: "Real-time project cost insights with minimal verification.",
      icon: <Database className="w-12 h-12" />,
    },
    {
      title: "Built for Your Team",
      desc: "Every worker, from PMs to Ironworkers, benefits from simplified, intuitive workflows.",
      icon: <UserPlus className="w-12 h-12" />,
    },
  ]

  return (
    <section
      id="benefits"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.benefits ? "opacity-100 bg-gradient-to-br from-[#1e3a8a]/80 to-[#0e1d34]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">Immediate Benefits for Your Team</h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Experience tangible improvements in your workflow from day one.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {benefitsData.map((benefit, index) => (
            <div
              key={index}
              className={`text-center p-6 rounded-xl bg-[#1e3a8a]/20 border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1`}
            >
              <div className="w-12 h-12 mb-4 text-blue-400">{benefit.icon}</div>
              <h3 className="text-xl font-bold mb-3 text-[#3080c1]">{benefit.title}</h3>
              <p className="text-blue-200">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------- AI Integration Section -------------- */
function AIIntegrationSection({ isVisible }) {
  const aiItems = [
    {
      title: "Myte Timesheets",
      desc: "Voice-driven, cost-coded time reporting.",
      link: "https://mytegroup.ai/timesheets",
      icon: <Mic className="w-8 h-8 text-blue-400 mb-3" />,
    },
    {
      title: "Myte Material Requests",
      desc: "Instantly request equipment and materials via voice.",
      link: "https://mytegroup.ai/equipment-request",
      icon: <Briefcase className="w-8 h-8 text-blue-400 mb-3" />,
    },
    {
      title: "Myte Business Cards",
      desc: "Speak or scan business cards—data structured automatically.",
      link: "https://mytegroup.ai/business-cards",
      icon: <CreditCard className="w-8 h-8 text-blue-400 mb-3" />,
    },
  ]

  return (
    <section
      id="ai"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.ai ? "opacity-100 bg-gradient-to-br from-[#0e1d34]/95 to-[#1e3a8a]/80" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
            Smart AI That Understands Construction
          </h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Our AI listens to your voice and transforms it into structured data—timesheets, material requests, and
            more—mapped perfectly to your projects and teams.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {aiItems.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-6 rounded-xl border border-[#3080c1]/30 flex flex-col transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1`}
            >
              {item.icon}
              <h3 className="text-xl font-bold mb-3 text-[#3080c1]">{item.title}</h3>
              <p className="text-blue-200 mb-4 flex-grow">{item.desc}</p>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-4 py-2 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300 text-center"
              >
                Try Now
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* -------------- Pricing Section -------------- */
function PricingSection({ isVisible, activeTab, setActiveTab }) {
  return (
    <section
      id="pricing"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.pricing ? "opacity-100 bg-gradient-to-br from-[#0e1d34]/90 to-[#1e3a8a]/90" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">Simple, Transparent Pricing</h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Invest in productivity with a solution that pays for itself.
          </p>
        </div>
        <div className="flex justify-center mb-8">
          <div
            className={`inline-flex bg-[#1e3a8a]/30 rounded-lg p-1 ${
              isVisible.pricing ? "opacity-100" : "opacity-0"
            } transition-opacity duration-300`}
          >
            <button
              onClick={() => setActiveTab("free")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === "free"
                  ? "bg-[#3080c1] text-white shadow-md"
                  : "text-blue-200 hover:text-white hover:bg-[#3080c1]/20"
              }`}
            >
              Free Trial
            </button>
            <button
              onClick={() => setActiveTab("paid")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === "paid"
                  ? "bg-[#3080c1] text-white shadow-md"
                  : "text-blue-200 hover:text-white hover:bg-[#3080c1]/20"
              }`}
            >
              Paid Plan
            </button>
            <button
              onClick={() => setActiveTab("custom")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === "custom"
                  ? "bg-[#3080c1] text-white shadow-md"
                  : "text-blue-200 hover:text-white hover:bg-[#3080c1]/20"
              }`}
            >
              Custom
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* FREE TRIAL */}
          {activeTab === "free" && (
            <div
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-8 rounded-xl border border-[#3080c1]/50 shadow-lg ${
                isVisible.pricing ? "opacity-100" : "opacity-0"
              } transition-opacity duration-300`}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Free Trial</h3>
                <div className="text-4xl font-bold text-white mb-2">$0</div>
                <p className="text-xl text-blue-200">30 days, no credit card required</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">1 Project</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">1 Foreman seat</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Voice-enabled timesheets</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Basic reporting</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Email support</span>
                </li>
              </ul>
              <div className="text-center">
                <Link
                  to="/joinMyte"
                  className="inline-flex items-center px-8 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
                >
                  Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          )}

          {/* PAID PLAN */}
          {activeTab === "paid" && (
            <div
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-8 rounded-xl border-2 border-[#3080c1] shadow-xl relative overflow-hidden ${
                isVisible.pricing ? "opacity-100" : "opacity-0"
              } transition-opacity duration-300`}
            >
              <div className="absolute top-0 right-0 bg-[#3080c1] text-white px-4 py-1 text-sm font-semibold">
                Most Popular
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Construction Suite</h3>
                <div className="text-4xl font-bold text-white mb-2">
                  $200<span className="text-xl font-normal">/month</span>
                </div>
                <p className="text-xl text-blue-200">per seat (foreman)</p>
              </div>
              <div className="bg-[#0e1d34]/30 p-4 rounded-lg mb-6">
                <p className="text-center text-xl font-bold text-blue-100">
                  Unlimited Projects, Unlimited Possibilities
                </p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">
                    Empower each foreman to save up to 5 hours weekly—just by speaking
                  </span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Voice-enabled timesheets with cost coding</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Real-time data validation and verification</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Seamless integration with existing systems</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Custom workflow automation</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Dedicated support and onboarding</span>
                </li>
              </ul>
              <div className="text-center">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-8 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Schedule Your Demo <Calendar className="ml-2 w-5 h-5" />
                  </a>
                  <Link
                    to="/joinMyte"
                    className="inline-flex items-center px-8 py-3 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Start Your Trial <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
                <p className="mt-4 text-blue-200">No credit card required. 30-day free trial available.</p>
              </div>
            </div>
          )}

          {/* CUSTOM ENTERPRISE */}
          {activeTab === "custom" && (
            <div
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-8 rounded-xl border border-[#3080c1]/50 shadow-lg ${
                isVisible.pricing ? "opacity-100" : "opacity-0"
              } transition-opacity duration-300`}
            >
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise Solutions</h3>
                <div className="text-4xl font-bold text-white mb-2">Custom Pricing</div>
                <p className="text-xl text-blue-200">Tailored to your specific needs</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">All features from the paid plan</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Custom integrations with your existing systems</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Dedicated account manager</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Custom AI model training for your specific needs</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">Priority support and feature requests</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3 flex-shrink-0" />
                  <span className="text-blue-100">On-site training and implementation</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
                >
                  Contact for Custom Quote <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a
                  href="https://www.mytecody.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-8 py-3 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
                >
                  Start Your Roadmap <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/* -------------- AI Demo CTA Section -------------- */
function AIDemoCTASection({ isVisible }) {
  return (
    <section
      id="demo"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.demo ? "opacity-100 bg-gradient-to-br from-[#1e3a8a] to-[#3080c1]/30" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">
          Ready to Transform Your Workflow?
        </h2>
        <p className="text-base md:text-lg text-blue-200 mb-8 max-w-3xl mx-auto">
          Book a live demo and see how Myte Construction saves your crew hours every week.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/joinMyte"
            className="px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
          >
            <Play className="mr-2 w-5 h-5" /> Watch Demos
          </Link>
          <a
            href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
          >
            <Calendar className="mr-2 w-5 h-5" /> Book Your Demo Now
          </a>
        </div>
      </div>
    </section>
  )
}

/* -------------- Community Section -------------- */
function CommunitySection({ isVisible }) {
  return (
    <section
      id="community"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.community ? "opacity-100 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a]" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">Trusted by Innovators</h2>
          <p className="text-base md:text-lg text-blue-200 max-w-3xl mx-auto">
            Join the growing community of forward-thinking businesses leveraging Myte's technology.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {communityPartners.map((partner, index) => (
            <div
              key={index}
              className={`text-center p-6 rounded-xl bg-[#1e3a8a]/20 border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1`}
            >
              <a href={partner.link} target="_blank" rel="noopener noreferrer">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#3080c1]/20 rounded-full blur-lg"></div>
                    <img
                      src={partner.avatar || "/placeholder.svg"}
                      alt={partner.name}
                      className="w-24 h-24 rounded-full border-2 border-[#3080c1] object-cover relative z-10"
                      loading="lazy"
                    />
                  </div>
                </div>
              </a>
              <a href={partner.link} target="_blank" rel="noopener noreferrer">
                <h3 className="text-xl font-bold mb-1 text-blue-100">{partner.name}</h3>
              </a>
              <p className="text-[#3080c1] mb-3">{partner.company}</p>
              <p className="text-blue-200">{partner.description}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <a
            href="https://mytegroup.ai/joinmyte"
            className="inline-block px-6 py-3 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg transition-all duration-300"
          >
            Join the Community
          </a>
        </div>
      </div>
    </section>
  )
}

/* -------------- Vision Section -------------- */
function VisionSection({ isVisible }) {
  return (
    <section
      id="vision"
      className={`py-16 px-4 transition-opacity duration-300 ${
        isVisible.vision ? "opacity-100 bg-gradient-to-b from-[#1e3a8a] to-[#0e1d34]" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-blue-100">Own Your Tech, Own Your Future</h2>
        <p className="text-base md:text-lg text-blue-200 mb-8 max-w-3xl mx-auto">
          Myte Construction isn't just software—it's your path to digital autonomy. Take control of your data and
          workflows in the AI era.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/joinMyte"
            className="inline-flex items-center px-8 py-4 bg-[#3080c1] hover:bg-[#2563eb] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
          >
            Start Your Free Trial Today <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
          <a
            href="https://www.mytecody.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-[#1e3a8a] hover:bg-[#1e40af] text-white rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
          >
            Start Your Roadmap <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

/* -------------- Footer -------------- */
function Footer() {
  return (
    <footer className="py-12 px-4 bg-[#0e1d34] border-t border-[#3080c1]/50">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-bold text-lg text-blue-100">Myte Construction</span>
            </div>
            <p className="text-sm text-blue-200 mb-4 max-w-md">
              Revolutionizing payroll management for field construction crews with AI-driven, voice-enabled technology.
            </p>
            <p className="text-xs text-blue-300">
              © {new Date().getFullYear()} Myte Construction. All rights reserved.
            </p>
          </div>
          <div className="md:col-span-3">
            <h3 className="text-lg font-bold mb-4 text-blue-100">Quick Links</h3>
            <ul className="flex flex-col gap-2">
              <li>
                <a href="#products" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Products
                </a>
              </li>
              <li>
                <a href="#features" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Features
                </a>
              </li>
              <li>
                <a href="#pricing" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Pricing
                </a>
              </li>
              <li>
                <a href="#community" className="text-blue-200 hover:text-white transition-colors duration-300">
                  Trusted by Innovators
                </a>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-lg font-bold mb-4 text-blue-100">Contact</h3>
            <ul className="flex flex-col gap-2">
              <li>
                <a
                  href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Schedule a Call
                </a>
              </li>
              <li>
                <Link
                  to="/myteconstruction/video-demos"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Video Demos
                </Link>
              </li>
              <li>
                <Link
                  to="/myteconstruction/login"
                  className="text-blue-200 hover:text-white transition-colors duration-300"
                >
                  Login
                </Link>
              </li>
            </ul>
          </div>
          <div className="md:col-span-2 flex justify-center md:justify-end items-start">
            <Link to="/myteconstruction" className="flex items-center">
              <img
                src="/LogoFile.png"
                alt="Myte Construction Logo"
                className="w-24 h-auto"
                loading="lazy"
              />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* -------------------------------------------------------------------------- */
/*                           4. MAIN LANDING PAGE                             */
/* -------------------------------------------------------------------------- */
export default function ALandingPage() {
  // Mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // Nav highlight
  const [activeSection, setActiveSection] = useState("hero")
  // Header scroll effect
  const [isScrolled, setIsScrolled] = useState(false)
  // Visibility states
  const [isVisible, setIsVisible] = useState({ hero: true })
  // Pricing toggle
  const [activeTab, setActiveTab] = useState("paid")

  // Tagline
  const [showTagline, setShowTagline] = useState(false)

  const typedElementRef = useRef(null)

  // Initialize typed headlines
  useFastTypewriter(typedElementRef, setShowTagline)

  // Scroll performance
  useEffect(() => {
    let lastScrollY = window.scrollY
    let ticking = false
    const handleScroll = () => {
      lastScrollY = window.scrollY
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(lastScrollY > 10)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Use the custom hook
  useActiveSection(setActiveSection)

  // IntersectionObserver for each section
  useEffect(() => {
    const sectionIds = ["products", "about", "features", "benefits", "ai", "pricing", "demo", "community", "vision"]
    const observerOptions = { root: null, rootMargin: "0px", threshold: 0.1 }
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id
          setIsVisible((prev) => ({ ...prev, [sectionId]: true }))
          sectionObserver.unobserve(entry.target)
        }
      })
    }, observerOptions)

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (el) sectionObserver.observe(el)
    })

    return () => {
      sectionObserver.disconnect()
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white relative">
      <MyteConstructionHelmet />

      {/* Header */}
      <Header
        isScrolled={isScrolled}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        activeSection={activeSection}
      />

      {/* Hero */}
      <HeroSection
        showTagline={showTagline}
        typedElementRef={typedElementRef}
      />

      {/* Products */}
      <ProductsSection isVisible={isVisible} />

      {/* About */}
      <AboutSection isVisible={isVisible} />

      {/* Features */}
      <FeaturesSection isVisible={isVisible} />

      {/* Benefits */}
      <BenefitsSection isVisible={isVisible} />

      {/* AI Integration */}
      <AIIntegrationSection isVisible={isVisible} />

      {/* Pricing */}
      <PricingSection isVisible={isVisible} activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* AI Demo CTA */}
      <AIDemoCTASection isVisible={isVisible} />

      {/* Community */}
      <CommunitySection isVisible={isVisible} />

      {/* Vision */}
      <VisionSection isVisible={isVisible} />

      {/* Footer */}
      <Footer />
    </div>
  )
}
